/**
 * Revamp copy of `PlanPageDetailsOPZ`'s `MultiDimSetting` — same behaviour,
 * revamp chrome. The shared one is used by several other pages, so its old
 * theme (white label text, teal dimension glyph) can't be changed in place.
 * Those colours were picked for the panel's old purple background; on the
 * revamp's white panel the white "(n / n)" label was invisible, leaving a
 * stray teal cube floating with no context.
 */
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Tooltip,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getBlockDetails,
  getBlockDimensions,
  getIndicatorTableData,
  resetMultiDimFilter,
  setFilterItems,
  setResetFilter,
  UpdatePlangPageIndicatorTableData,
} from 'redux/PlanPageSlice';
import { accent, text as textToken } from 'styles/revampTokens';
import { getBaseScenario } from 'redux/ScenarioSlice';

import FigmaBlockDimensionsIcon from '../icons/FigmaBlockDimensionsIcon';

const SettingsPopoverContent = PopoverContent as React.ElementType;
const SettingsPopoverButton = Button as React.ElementType;
const SettingsTooltip = Tooltip as React.ElementType;
const DimensionSwitch = Switch as React.ElementType;

interface Properties {
  refetchRequest: () => void;
  indicatorId: any;
}

const MultiDimSettingRevamp = ({ indicatorId, refetchRequest }: Properties) => {
  const dispatch: any = useDispatch();
  const indicatorTableData = useSelector(getIndicatorTableData);
  const baseScenario = useSelector(getBaseScenario);

  const dimensionList = useSelector(getBlockDetails);
  const dimensionItemList = useSelector(getBlockDimensions);
  const [dimensionData, setDimensionData] = useState(dimensionList?.dimensions || []);
  const [activeDim, setActiveDim] = useState<any>();

  useEffect(() => {
    const dimensionsList = dimensionList?.dimensions;
    setDimensionData(dimensionsList);
  }, [dimensionList?.dimensions]);
  useEffect(() => {
    const active = indicatorTableData?.dimensions?.length
      ? Number(indicatorTableData?.dimensions?.length) + 1
      : 1;
    setActiveDim(active);
  }, [indicatorTableData]);

  const doesDimensionExist = (dim: any, dimArray: any[]) => {
    if (dim?.name?.toLowerCase() === 'time') return true;
    const exists = dimArray?.some((data) => Number(data?.id) === dim?.id);
    return exists;
  };
  function addDim(currentDim: any, newDim: any) {
    let found = false;
    const updatedDim = JSON.parse(JSON.stringify(currentDim));
    updatedDim.forEach((dim: any, index: any) => {
      if (dim.id === String(newDim.id)) {
        updatedDim.splice(index, 1);
        found = true;
      }
    });
    if (!found) {
      updatedDim.push({ id: String(newDim.id) });
    }
    return updatedDim;
  }

  const handleSettingChange = async (data: any) => {
    const dim = indicatorTableData?.dimensions;
    const result = await addDim(dim, data);
    const payload = {
      indicator_id: indicatorId,
      request_body: {
        dimensions: result,
        bulk_input: true,
        type: indicatorTableData?.type,
        format_excel_data: true,
        scenario_id: baseScenario?.id,
      },
    };
    await dispatch(resetMultiDimFilter());
    await dispatch(setFilterItems([]));
    await dispatch(setResetFilter(true));
    await dispatch(UpdatePlangPageIndicatorTableData(payload));
    await refetchRequest();
  };

  const hasDimensionItems = (id: number) => {
    const dimension = dimensionItemList?.dimensions?.find((dim: any) => dim.id === id);

    if (dimension?.items.length > 0) {
      return true;
    }
    return false;
  };

  const renderMenuList = (list: any) => {
    const filteredList: any = [...(list || [])];
    return (
      <Box maxH={'190px'} overflowY={'auto'} className='scroll'>
        {filteredList?.map((data: any, index: number) => (
          <div
            key={index}
            style={{
              alignItems: 'center',
              display: 'flex',
              listStyleType: 'none',
              padding: '.5rem 0',
            }}
          >
            <DimensionSwitch
              mr={2}
              size='sm'
              sx={{
                // Chakra's default switch is blue; the revamp's only
                // interaction colour is the purple accent.
                '.chakra-switch__track[data-checked]': { background: accent.solid },
              }}
              disabled={
                data?.name === 'time' || data?.name === 'Time' || !hasDimensionItems(data?.id)
              }
              isChecked={doesDimensionExist(data, indicatorTableData?.dimensions)}
              onChange={() => handleSettingChange(data)}
            />
            {!hasDimensionItems(data?.id) ? (
              data?.name === 'time' || data?.name === 'Time' ? (
                data?.name
              ) : (
                <SettingsTooltip label='Dimension has no items'>
                  <p> {data?.name}</p>
                </SettingsTooltip>
              )
            ) : (
              data?.name
            )}
          </div>
        ))}
      </Box>
    );
  };

  return (
    <div style={{ alignItems: 'center', display: 'flex', width: '100px' }}>
      <Popover closeOnBlur placement='bottom-end'>
        <PopoverTrigger>
          <SettingsPopoverButton
            color={textToken.secondary}
            fontSize={'14px'}
            transition='all 0.2s'
            variant={'unstyled'}
            display={'flex'}
            alignItems={'center'}
            h={'auto'}
            p={0}
          >
            <SettingsTooltip label='Block Dimensions'>
              <div style={{ alignItems: 'center', display: 'flex' }}>
                <FigmaBlockDimensionsIcon width={13} height={13} color={textToken.secondary} /> (
                {activeDim} /{dimensionData?.length || 0}) <ChevronDownIcon />
              </div>
            </SettingsTooltip>
          </SettingsPopoverButton>
        </PopoverTrigger>
        <SettingsPopoverContent style={{ background: 'white', maxHeight: 250, width: 230 }}>
          <div style={{ paddingBottom: 0, paddingLeft: 16, paddingRight: 16, color: 'black' }}>
            {renderMenuList(dimensionData)}
          </div>
        </SettingsPopoverContent>
      </Popover>
    </div>
  );
};

export default MultiDimSettingRevamp;
